import { useEffect, useRef, useState } from "react";
import { CvBox } from "@/components/landing/CvSnapshot";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// BoxAnnotator: shows the image inside an aspect-16:10 container. User drags to draw a new box
// (or clicks an existing box to edit). Boxes are stored as % of image area: { x, y, w, h, label, color, blink, border_style, confidence }
export default function BoxAnnotator({ src, boxes, onChange, camId = "CAM-01", disabled }) {
  const frameRef = useRef(null);
  const [drag, setDrag] = useState(null); // { startX, startY, currentX, currentY }
  const [editing, setEditing] = useState(null); // { index, box }

  const beginDrag = (e) => {
    if (disabled) return;
    if (e.button !== 0) return;
    const rect = frameRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setDrag({ startX: x, startY: y, currentX: x, currentY: y });
  };

  const moveDrag = (e) => {
    if (!drag) return;
    const rect = frameRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setDrag((d) => ({ ...d, currentX: x, currentY: y }));
  };

  const endDrag = () => {
    if (!drag) return;
    const x = Math.min(drag.startX, drag.currentX);
    const y = Math.min(drag.startY, drag.currentY);
    const w = Math.abs(drag.currentX - drag.startX);
    const h = Math.abs(drag.currentY - drag.startY);
    setDrag(null);
    if (w < 2 || h < 2) return; // ignore tiny drags
    const next = {
      x: round1(x), y: round1(y), w: round1(w), h: round1(h),
      label: "LABEL", color: "lime", blink: false, border_style: "solid", confidence: null,
    };
    onChange([...(boxes || []), next]);
    setEditing({ index: boxes.length, box: next });
  };

  const updateBox = (i, patch) => {
    const next = boxes.map((b, idx) => (idx === i ? { ...b, ...patch } : b));
    onChange(next);
    if (editing && editing.index === i) setEditing({ index: i, box: { ...editing.box, ...patch } });
  };

  const removeBox = (i) => {
    onChange(boxes.filter((_, idx) => idx !== i));
    setEditing(null);
  };

  const previewBox = drag && {
    x: Math.min(drag.startX, drag.currentX),
    y: Math.min(drag.startY, drag.currentY),
    w: Math.abs(drag.currentX - drag.startX),
    h: Math.abs(drag.currentY - drag.startY),
  };

  return (
    <div className="space-y-3" data-testid="box-annotator">
      <div className="flex items-center justify-between">
        <p className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">
          Click & drag on the image to add a box. Click any box to edit.
        </p>
        <p className="font-mono-ui text-[10px] text-neutral-500">{boxes?.length || 0} box{(boxes?.length || 0) === 1 ? "" : "es"}</p>
      </div>
      <div
        ref={frameRef}
        data-testid="annotator-frame"
        className="cv-frame w-full aspect-[16/10] cursor-crosshair select-none"
        onMouseDown={beginDrag}
        onMouseMove={moveDrag}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
      >
        {src ? (
          <img src={src} alt={camId} className="cv-img absolute inset-0 w-full h-full object-cover pointer-events-none" />
        ) : (
          <div className="absolute inset-0 bg-neutral-900 flex items-center justify-center pointer-events-none">
            <p className="font-mono-ui text-xs text-neutral-400">Upload an image to begin annotating</p>
          </div>
        )}
        <div className="cv-crosshair" />
        <div className="cv-hud top-2 left-2 font-mono-ui">{camId}</div>
        <div className="cv-hud top-2 right-2"><span className="cv-live-dot" />LIVE</div>

        {(boxes || []).map((b, i) => (
          <div
            key={i}
            onMouseDown={(e) => {
              e.stopPropagation();
              setEditing({ index: i, box: b });
            }}
            data-testid={`annotator-box-${i}`}
            className="absolute"
            style={{ left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%`, cursor: "pointer" }}
          >
            <CvBox box={b} index={i} />
          </div>
        ))}

        {previewBox && (
          <div
            className="absolute border-2 border-dashed border-[#84CC16] bg-[#84CC16]/10 pointer-events-none"
            style={{ left: `${previewBox.x}%`, top: `${previewBox.y}%`, width: `${previewBox.w}%`, height: `${previewBox.h}%` }}
          />
        )}
      </div>

      {/* Box editor */}
      {editing && (
        <div data-testid="annotator-editor" className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">
              Editing box #{editing.index + 1}
            </p>
            <button
              type="button"
              data-testid="annotator-editor-close"
              onClick={() => setEditing(null)}
              className="text-xs text-neutral-500 hover:text-neutral-900"
            >
              Done
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <L>Label</L>
              <Input
                data-testid="annotator-input-label"
                value={editing.box.label}
                onChange={(e) => updateBox(editing.index, { label: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <L>Color</L>
              <Select
                value={editing.box.color}
                onValueChange={(v) => updateBox(editing.index, { color: v })}
              >
                <SelectTrigger data-testid="annotator-select-color"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lime">Lime / Green</SelectItem>
                  <SelectItem value="amber">Amber / Orange</SelectItem>
                  <SelectItem value="red">Red / Alert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <L>Border</L>
              <Select
                value={editing.box.border_style || "solid"}
                onValueChange={(v) => updateBox(editing.index, { border_style: v })}
              >
                <SelectTrigger data-testid="annotator-select-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="solid">Solid</SelectItem>
                  <SelectItem value="dotted">Dotted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <L>Mode</L>
              <Select
                value={editing.box.blink ? "blink" : "fixed"}
                onValueChange={(v) => updateBox(editing.index, { blink: v === "blink" })}
              >
                <SelectTrigger data-testid="annotator-select-blink"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed</SelectItem>
                  <SelectItem value="blink">Blink</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <L>Confidence %</L>
              <Input
                data-testid="annotator-input-confidence"
                type="number"
                min={0}
                max={100}
                value={editing.box.confidence ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  const num = raw === "" ? null : Math.max(0, Math.min(100, Number(raw)));
                  updateBox(editing.index, { confidence: num });
                }}
                placeholder="-"
              />
            </div>
            {/* x/y/w/h numeric */}
            {["x", "y", "w", "h"].map((k) => (
              <div key={k} className="md:col-span-3">
                <L>{k.toUpperCase()} (%)</L>
                <Input
                  data-testid={`annotator-input-${k}`}
                  type="number"
                  step="0.5"
                  min={0}
                  max={100}
                  value={editing.box[k]}
                  onChange={(e) => updateBox(editing.index, { [k]: Number(e.target.value) })}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-2">
            <Button
              type="button"
              data-testid="annotator-delete-box"
              onClick={() => removeBox(editing.index)}
              variant="destructive"
              className="font-semibold"
            >
              Delete box
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const L = ({ children }) => (
  <span className="block font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">
    {children}
  </span>
);

function round1(n) {
  return Math.round(n * 10) / 10;
}
